import { t } from "@/lib/i18n"

/**
 * Sending a receipt: fetch the PDF the server composed, then hand the FILE to
 * whatever the device uses to share.
 *
 * **A file, not a link.** The URL is scoped to a signed-in staff member at one
 * restaurant, so it would open nothing on the customer's phone; and a link that
 * did open would be a bearer key to what a table ate. What travels is bytes.
 *
 * **The button never hides.** Web Share needs a user gesture and a secure
 * context, and several desktop browsers do not implement it at all — but the
 * tablet on the floor does, which is where this is used. So the same control
 * shares where it can and downloads where it cannot, rather than disappearing on
 * half the machines that open the drawer.
 *
 * Sharing FILES is the least supported corner of the standard, and `canShare`
 * is the only honest way to ask: `navigator.share` existing says nothing about
 * whether this device will accept a PDF.
 */
export type ReceiptShareOutcome = "shared" | "cancelled" | "downloaded"

export async function shareReceipt(bill: { id: number; total: string }): Promise<ReceiptShareOutcome> {
  const response = await fetch(`/bills/${bill.id}/receipt`, {
    headers: { Accept: "application/pdf" },
    credentials: "same-origin",
  })

  if (!response.ok) {
    throw new Error(`receipt request failed with ${response.status}`)
  }

  const filename = `recibo-cuenta-${bill.id}.pdf`
  const file = new File([ await response.blob() ], filename, { type: "application/pdf" })

  if (canShareFile(file)) {
    try {
      await navigator.share({
        files: [ file ],
        title: t("ui.react.features.staff.receipt_share.title"),
        text: t("ui.react.features.staff.receipt_share.text", { id: bill.id }),
      })
      return "shared"
    } catch (error) {
      // Closing the sheet without picking anyone is a decision, not a failure.
      // It arrives as an exception all the same, and reporting it would tell a
      // cashier something went wrong when nothing did.
      if (error instanceof DOMException && error.name === "AbortError") return "cancelled"
      throw error
    }
  }

  download(file, filename)
  return "downloaded"
}

function canShareFile(file: File): boolean {
  // `navigator.share` on its own is not the question: a browser can implement
  // sharing and still refuse files.
  return typeof navigator.canShare === "function" && navigator.canShare({ files: [ file ] })
}

function download(file: File, filename: string) {
  const url = URL.createObjectURL(file)
  const link = document.createElement("a")

  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Freed on the next tick rather than immediately: revoking while the click is
  // still being handled cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
